<script lang="ts">
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import Avatar from "$components/+shared/Avatar/Avatar.svelte";
  import { communitySlug } from "$stores/community";
  import Upvote from "$components/forum/Upvotes/Upvote.svelte";
  import api from "api";
  import IconButtonTrigger from "$components/+design-system/Dropdown/fields/IconButtonTrigger.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import CreationTimeInterval from "$components/+shared/CreationTimeInterval.svelte";
  import { labels, labelsFetched } from "app/stores/labels";
  import { tooltip } from "app/actions/tooltip";
  import cx from "classnames";
  import type { Thread } from "models/forums";
  import type { DropdownAction } from "$components/+design-system/Dropdown/fields/Action.svelte";
  import {
    EyeSlash,
    VerticalDots,
    LockSolid,
    PinFilled,
    Right,
    CheckFilled,
    DashedCircle,
  } from "$components/+shared/OVIcon";

  export let thread: Thread;
  export let menuOptions: DropdownAction[];
  export let canUpvote: boolean;
  export let isThreadUpvotesEnabled: boolean;
  export let showBreadCrumb: boolean = false;
  export let showChannelColour: boolean = false;

  let highValueActivityCount: number;
  $: {
    if (thread.isSubscribedToActivity !== undefined) {
      highValueActivityCount =
        (thread.isSubscribedToActivity ? thread.newRepliesCount! : 0) + thread.directRepliesCount!;
    }
  }

  const MAX_LABELS_VISIBLE = 2;
</script>

<div
  class="flex flex-col gap-4 py-3 pl-2 font-semibold hover:bg-gray-900/2 lg:flex-row lg:items-center lg:gap-0 dark:hover:bg-white/2"
  class:border-l-2={showChannelColour}
  style={showChannelColour ? `border-left-color: ${thread.forumSpace.color};` : ""}
>
  <div class="flex items-center gap-2 overflow-hidden lg:mr-6">
    {#if isThreadUpvotesEnabled}
      <Upvote
        variant="vertical-small"
        disabled={!canUpvote}
        count={thread.upvoteCount}
        isSelected={thread.userUpvoted}
        addUpvote={() =>
          api.communitiesThreadsUpvote({
            communitySlug: $communitySlug,
            uuid: thread.uuid,
          })}
        removeUpvote={() =>
          api.communitiesThreadsRemoveUpvote({
            communitySlug: $communitySlug,
            uuid: thread.uuid,
          })}
      />
    {/if}

    {#if thread.author}
      <Avatar
        mainImage={thread.author.profileImage}
        backgroundColor={thread.author.profileColor}
        label={thread.author.handle && thread.author.handle[0]}
        size="size-7"
      />
    {/if}
    {#if thread.solvable}
      <OVIcon
        icon={thread.solution ? CheckFilled : DashedCircle}
        class={cx(thread.solution ? "text-green-400" : "text-gray-400", "shrink-0")}
      />
    {/if}

    {#if thread.locked || thread.pinned || thread.hidden}
      <div class="flex gap-2">
        {#if thread.locked}
          <OVIcon icon={LockSolid} class="text-gray-500 dark:text-gray-400" />
        {/if}
        {#if thread.pinned}
          <OVIcon icon={PinFilled} class="text-gray-500 dark:text-gray-400" />
        {/if}
        {#if thread.hidden}
          <OVIcon icon={EyeSlash} color="#F5656C" />
        {/if}
      </div>
    {/if}

    <h3
      class="line-clamp-1 shrink-[1] text-sm font-medium text-gray-700 md:text-[13px] lg:line-clamp-none lg:block lg:truncate dark:text-gray-200"
    >
      {thread.title}
    </h3>
    {#if showBreadCrumb}
      <div
        class="hidden min-w-[12ch] shrink-[2] items-center gap-1 overflow-hidden text-sm font-normal text-neutral-500 md:text-[13px] lg:flex dark:text-neutral-400"
      >
        <OVIcon icon={Right} size="size-3" />
        <span class="w-auto truncate" title={thread.forumSpace.title}>
          {thread.forumSpace.title}
        </span>
      </div>
    {/if}
  </div>

  <div class="flex grow items-center justify-end gap-4 pr-2 text-sm md:text-[13px]">
    <div class="-mb-0.5 mr-auto w-[120px] truncate pr-2 lg:hidden">
      <CreationTimeInterval time={thread.created} modified={new Date().toISOString()} />
    </div>

    <div class="mr-1">
      {#if !!highValueActivityCount}
        <span
          title="{highValueActivityCount} notifications"
          class={cx(
            "ml-2 flex h-[18px] min-w-[18px] items-center justify-center rounded",
            "bg-green-400/50 px-[5px] text-xxs font-semibold text-white"
          )}
        >
          {highValueActivityCount}
        </span>
      {/if}
    </div>
    {#if $labelsFetched && thread.labels.length}
      <div class="mr-1 hidden gap-1.5 md:flex">
        {#each thread.labels.slice(0, MAX_LABELS_VISIBLE) as uuid (uuid)}
          {@const label = $labels[uuid]}
          <div
            class="flex w-fit items-center gap-1 overflow-hidden rounded bg-black/5 px-1 py-[2px] dark:bg-white/5"
          >
            <span style:color={label.color} class="text-md mb-[1px] leading-[0]">•</span>
            <span class="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
              {label.name}
            </span>
          </div>
        {/each}
        {#if thread.labels.length > MAX_LABELS_VISIBLE}
          <span
            use:tooltip
            title={thread.labels
              .slice(MAX_LABELS_VISIBLE)
              .map((uuid) => $labels[uuid].name)
              .join(", ")}
            class="rounded bg-black/5 px-1 py-[2px] text-xs font-medium text-gray-500 dark:bg-white/5 dark:text-gray-400"
          >
            +{thread.labels.length - MAX_LABELS_VISIBLE}
          </span>
        {/if}
      </div>
    {/if}

    <div class="flex lg:w-[80px]">
      <span class="text-xs text-sky-500 dark:text-sky-400">
        <span class="ml-1">{thread.replyCount}</span>
        {thread.replyCount === 1 ? "reply" : "replies"}
      </span>
    </div>

    <div class="hidden w-[80px] truncate lg:block">
      <CreationTimeInterval time={thread.created} modified={new Date().toISOString()} />
    </div>

    {#if menuOptions.length}
      <Dropdown>
        <IconButtonTrigger
          customIconSize="h-[18px]"
          slot="trigger"
          let:active
          {active}
          icon={VerticalDots}
          a11yLabel="More actions"
        />
        {#each menuOptions as option}
          <Action title={option.title} icon={option.icon} on:click={option.onClick} />
        {/each}
      </Dropdown>
    {/if}
  </div>
</div>
